/** Library: all maps, newest first, with one-tap creation. */
import type { MapKind } from '../../shared/doc.ts';
import { pickImages, imageFilesFrom } from '../app/image.ts';
import { createMap, humanDate, kindLabel, mapThumb } from '../app/maps.ts';
import { warmMap } from '../app/warm.ts';
import { app, type SyncStatus } from '../app/state.ts';
import { navigate } from '../app/router.ts';
import type { LocalMap } from '../store/idb.ts';
import { h, icon, ICONS, toast, clear } from './dom.ts';
import { openSettings } from './settings.ts';
import { openSheet } from './sheet.ts';
import * as zoom from './zoom.ts';

/** Syncing is never shown while it works. Only when it cannot: the maps not yet in the cloud say so, quietly. */
const TROUBLE: ReadonlySet<SyncStatus> = new Set(['offline', 'error']);

/** Where the library was scrolled to when a map was opened: coming back, it is where it was left. */
let leftAt = 0;

interface Card {
  el: HTMLButtonElement;
  name: HTMLElement;
  when: HTMLElement;
  counts: HTMLElement;
  pic: HTMLElement;
  /** What the card last showed: it is touched again only when one of these differs. */
  text: string;
  stamp: number | null;
  url: string | null;
}

export function renderLibrary(root: HTMLElement): () => void {
  const grid = h('div', { class: 'grid' });
  const busy = h('div', { class: 'glass busy', role: 'status' }, h('div', { class: 'spinner' }), 'Preparing image…');
  const cards = new Map<string, Card>();
  let empty: HTMLElement | null = null;
  let alive = true;

  const start = async (kind: MapKind, files?: File[]) => {
    const picked = files ?? (await pickImages());
    const file = picked[0];
    if (!file) return;
    busy.classList.add('show');
    try {
      const map = await createMap(kind, file);
      navigate({ name: 'editor', id: map.id });
    } catch (e) {
      console.error(e);
      toast('Could not open that image.', 'error');
    } finally {
      busy.classList.remove('show');
    }
  };

  // The maps have the screen. What floats over them keeps to the edges: Settings top right, and the
  // two ways to begin at the bottom, under the thumbs, where the editor's tools are.
  const newBtn = (kind: MapKind) => h('button', { class: `tinted new ${kind}`, type: 'button', 'aria-label': `New ${kindLabel(kind)} map`, onClick: () => void start(kind) }, icon(ICONS.plus, 20), h('span', null, kindLabel(kind)));
  const settings = h('button', { class: 'glass round lib-top', type: 'button', 'aria-label': 'Settings', onClick: () => openSettings() }, icon(ICONS.settings));
  const dock = h('div', { class: 'glass lib-dock', role: 'group', 'aria-label': 'New map' }, newBtn('soils'), newBtn('concrete'));
  const body = h('div', { class: 'library' }, h('h1', { class: 'large-title' }, 'MapMark'), grid);
  // come back to by a zoom, it arrives whole: no entrance of its own under the landing plan
  const byZoom = zoom.arrivingBack();
  const screen = h('div', { class: 'screen' + (byZoom ? ' by-zoom' : '') }, body, h('div', { class: 'lib-edge top' }), h('div', { class: 'lib-edge bottom' }), settings, dock, busy);
  root.append(screen);
  // the maps dissolve under the floating controls only once they have scrolled under them
  body.addEventListener('scroll', () => screen.classList.toggle('scrolled', body.scrollTop > 8), { passive: true });

  // Paste / drop an image anywhere on the library → ask which kind.
  const onFiles = (files: File[]) => {
    if (!files.length) return;
    if (document.querySelector('dialog[open]')) return;
    const s = openSheet('New map from this image', { center: true });
    const pick = (kind: MapKind) => h('button', { class: `tinted ${kind}`, type: 'button', onClick: () => (s.close(), void start(kind, files)) }, kindLabel(kind));
    s.body.append(h('div', { class: 'choice' }, pick('soils'), pick('concrete')));
  };
  const onPaste = (e: ClipboardEvent) => onFiles(imageFilesFrom(e.clipboardData));
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    onFiles(imageFilesFrom(e.dataTransfer));
  };
  const onDragOver = (e: DragEvent) => e.preventDefault();
  document.addEventListener('paste', onPaste);
  screen.addEventListener('drop', onDrop);
  screen.addEventListener('dragover', onDragOver);

  /* A card is made once and lives as long as its map. When the list changes — a sync, a save —
   * each card is compared with what it already shows and touched only where that differs; a new
   * thumbnail is decoded before it replaces the old one. Nothing is rebuilt, so nothing flashes. */
  const countsOf = (m: LocalMap): string => {
    const count = (t: string) => m.doc.items.filter((i) => i.t === t).length;
    const say = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
    const main = say(count(m.kind === 'soils' ? 'test' : 'callout'), m.kind === 'soils' ? 'test' : 'callout');
    const shots = count('elev');
    return shots ? `${main} · ${say(shots, 'elevation')}` : main;
  };
  const build = (m: LocalMap): Card => {
    const pic = h('div', { class: 'thumb-img' }, icon(ICONS.photo, 28));
    const name = h('div', { class: 'name' });
    const when = h('span');
    const counts = h('span', { class: 'extra' });
    const el: HTMLButtonElement = h(
      'button',
      {
        class: 'card',
        type: 'button',
        dataset: { id: m.id },
        // the finger is down: the plan is read and decoded while the tap is still becoming a tap
        onPointerdown: () => warmMap(m.id),
        onClick: async () => {
          // the card's picture grows into the plan (zoom.ts)
          leftAt = body.scrollTop;
          const now = app.state.maps.find((x) => x.id === m.id) ?? m;
          await zoom.prepareOpen(m.id, el, now.doc.w, now.doc.h);
          navigate({ name: 'editor', id: m.id });
        },
      },
      pic,
      h('span', { class: `badge ${m.kind}` }, kindLabel(m.kind).toUpperCase()),
      h('div', { class: 'body' }, name, h('div', { class: 'meta' }, h('span', { class: 'unsent-mark', title: 'Not in the cloud yet' }, icon(ICONS.cloudOff, 15)), when, h('span', { class: 'extra' }, '·'), counts)),
    );
    return { el, name, when, counts, pic, text: '', stamp: null, url: null };
  };
  const loadThumb = async (c: Card, m: LocalMap, stamp: number) => {
    const blob = await mapThumb(m.id);
    if (!blob || !alive || c.stamp !== stamp) return;
    const url = URL.createObjectURL(blob);
    try {
      const img = new Image();
      img.src = url;
      await img.decode();
    } catch {
      /* show it anyway */
    }
    if (!alive || c.stamp !== stamp) return URL.revokeObjectURL(url);
    // the first picture fades in; a newer one, already decoded, dissolves in over the old, which leaves after
    const old = c.pic.querySelector('.pic');
    const oldUrl = c.url;
    const layer = h('div', { class: old ? 'pic newer' : 'pic', style: { backgroundImage: `url("${url}")` } });
    c.pic.append(layer);
    c.url = url;
    const retire = () => {
      old?.remove();
      if (oldUrl) URL.revokeObjectURL(oldUrl);
    };
    if (old) {
      layer.addEventListener('animationend', retire, { once: true });
      setTimeout(retire, 400);
    }
  };
  let fresh = !byZoom;
  const renderCards = (maps: LocalMap[]) => {
    if (!maps.length) {
      empty ??= h('div', { class: 'empty-state' }, icon(ICONS.photo, 44), h('h2', null, 'No maps yet'), h('p', null, 'Tap Soils or Concrete below, pick a plan screenshot from Photos, and start marking up.'));
      if (!empty.isConnected) grid.append(empty);
    } else empty?.remove();
    const seen = new Set<string>();
    maps.forEach((m, i) => {
      seen.add(m.id);
      let c = cards.get(m.id);
      if (!c) {
        c = build(m);
        cards.set(m.id, c);
        // the maps rise in once, when the library opens; a map that arrives later simply is there
        if (fresh) {
          c.el.classList.add('rise');
          c.el.style.setProperty('--i', String(i));
        }
      }
      const text = `${m.title}|${humanDate(m)}|${countsOf(m)}`;
      if (c.text !== text) {
        c.text = text;
        c.name.textContent = m.title;
        c.when.textContent = humanDate(m);
        c.counts.textContent = countsOf(m);
      }
      c.el.classList.toggle('unsent', m.dirty || !m.imageUploaded);
      const stamp = m.thumbStamp ?? 0;
      if (c.stamp !== stamp) {
        c.stamp = stamp;
        void loadThumb(c, m, stamp);
      }
      if (grid.children[i] !== c.el) grid.insertBefore(c.el, grid.children[i] ?? null);
    });
    for (const [id, c] of cards) {
      if (seen.has(id)) continue;
      c.el.remove();
      if (c.url) URL.revokeObjectURL(c.url);
      cards.delete(id);
    }
    fresh = false;
  };
  const renderTrouble = (s: SyncStatus) => grid.classList.toggle('trouble', TROUBLE.has(s));

  renderCards(app.state.maps);
  renderTrouble(app.state.sync);
  body.scrollTop = leftAt;
  const unsub = app.subscribe((s, prev) => {
    if (s.maps !== prev.maps) renderCards(s.maps);
    if (s.sync !== prev.sync) renderTrouble(s.sync);
  });
  return () => {
    alive = false;
    unsub();
    document.removeEventListener('paste', onPaste);
    for (const c of cards.values()) if (c.url) URL.revokeObjectURL(c.url);
    clear(grid);
    screen.remove();
  };
}
